import { useQuery } from '@tanstack/react-query'
import { RefObject, useEffect, useRef, useState } from 'react'
import { FieldErrors, Mode, useForm, UseFormGetValues, UseFormRegister, UseFormSetValue } from 'react-hook-form'

import { fetchGlobalParticipatedAds } from '@/api/globalParticipatedAd'
import { fetchHelpRequestTypes } from '@/api/helpRequestType'
import { fetchParticipatedAds } from '@/api/participatedAd'
import { publisherQueryOptions } from '@/api/publisher'
import { EMAIL_HOSTS } from '@/constants/email'
import { useQueryParams } from '@/hooks/useQueryParams'
import { sharedWeb } from '@/lib'
import getAxiosInstances from '@/lib/axios'
import { GlobalParams, HelpRequestType, LegacyParams, ParticipatedAd, ParticipatedEvent } from '@/types/supports'
import { toSnakeCase } from '@/utils/caseConverter'

const REQUIRED_CAMPAIGN_CODES = ['reward', 'reward_revoke']

export interface SupportFormData {
  helpRequestTypeId: string
  adIdx: string
  eventIdx: string
  content: string
  emailId: string
  eventTitle: string
  emailHost: string
  emailHostIdx: string
  agreement: boolean
  attachments: File[]
}

export interface UseSupportFormProps {
  publisher: string
  mode?: Mode
  enableMultiReward?: boolean
  searchParams?: Record<string, string>
  onSubmitSuccess?: (data: SupportFormData) => void
  onSubmitError?: (errors: Record<string, any>) => void
}

export interface UseSupportFormReturn {
  // 폼 관련
  register: UseFormRegister<SupportFormData>
  handleSubmit: (
    onSuccess: (data: SupportFormData) => void,
    onError?: (errors: FieldErrors<SupportFormData>) => void
  ) => Promise<void> | void
  errors: FieldErrors<SupportFormData>
  watch: ReturnType<typeof useForm<SupportFormData>>['watch']
  getValues: UseFormGetValues<SupportFormData>
  setValue: UseFormSetValue<SupportFormData>

  // 파일 처리 관련
  attachFile: (fileList: FileList | null, fileInputRef: RefObject<HTMLInputElement | null>) => Promise<File[] | null>
  detachFile: (fileInputRef: RefObject<HTMLInputElement | null>) => void

  // 데이터
  helpRequestTypes: HelpRequestType[]
  participatedAds: ParticipatedAd[]
  participatedEvents: ParticipatedEvent[]

  // 상태 플래그
  isContentEditable: boolean
  isRequestTypeEvent: boolean
  isGlobalCampaign: boolean
  isDirectEmail: boolean
  shouldSubmit: boolean
  isSubmitting: boolean

  // 메타데이터
  publisher: string
  rewardServiceNameInFooter: string
  headless: string
}

export function useSupportForm({ publisher, mode, enableMultiReward, searchParams = {} }: UseSupportFormProps) {
  const { queryParams: legacyParams, getParam } = useQueryParams<LegacyParams>()

  const { queryParams: globalParams } = useQueryParams<GlobalParams>()

  const { data: publisherData } = useQuery({
    ...publisherQueryOptions.list(legacyParams as LegacyParams),
    enabled: !!getParam('appId')
  })

  const { rewardServiceName = '', rewardServiceNameTopicParticle = '' } = publisherData?.publisher || {}

  const rewardServiceNameInFooter = rewardServiceName + rewardServiceNameTopicParticle

  const [helpRequestTypes, setHelpRequestTypes] = useState<HelpRequestType[]>([])
  const [participatedAds, setParticipatedAds] = useState<ParticipatedAd[]>([])
  const [participatedEvents, setParticipatedEvents] = useState<ParticipatedEvent[]>([])

  const {
    register,
    handleSubmit: hookFormSubmit,
    getValues,
    setValue,
    formState, //: { errors, isSubmitting },
    setError,
    watch
  } = useForm<SupportFormData>({
    mode: mode || 'onSubmit',
    defaultValues: {
      helpRequestTypeId: '',
      adIdx: '',
      eventIdx: '',
      eventTitle: '',
      content: '',
      emailId: '',
      emailHost: '',
      emailHostIdx: '',
      agreement: false,
      attachments: []
    },
    resolver: async (data, context) => validateFormData(data),
    shouldFocusError: false // 에러 필드로 자동 포커스 비활성화
  })

  const formData = watch()

  const watchHelpRequestTypeId = watch('helpRequestTypeId')
  const [isContentEditable, setIsContentEditable] = useState(false)
  const headless = searchParams && searchParams['headless'] ? searchParams['headless'] : ''

  const isRequestTypeEvent = helpRequestTypes.some(item => {
    const isRequiredCode = REQUIRED_CAMPAIGN_CODES.includes(item.code)
    const isMatchingId = Number(item.id) === Number(watchHelpRequestTypeId)

    return isRequiredCode && isMatchingId
  })

  useEffect(() => {
    setIsContentEditable(!!watchHelpRequestTypeId)

    // content 템플릿 적용
    const template = helpRequestTypes.find(o => o.id == watchHelpRequestTypeId)
    setValue('content', template?.placeHolder || '')

    setValue('adIdx', '')
  }, [watchHelpRequestTypeId])

  const [isGlobalCampaign, setIsGlobalCampaign] = useState(false)
  const [selectedAd, setSelectedAd] = useState<ParticipatedAd>()
  const [selectedEvent, setSelectedEvent] = useState<ParticipatedEvent>()

  const watchAdIdx = watch('adIdx')
  useEffect(() => {
    const selectedAd = participatedAds.find((ad, index) => watchAdIdx && index == Number(watchAdIdx))
    setSelectedAd(selectedAd)

    const isGlobalCampaign = (selectedAd && selectedAd?.type == 'global') || false
    setIsGlobalCampaign(isGlobalCampaign)
    setParticipatedEvents(selectedAd?.events || [])

    setValue('eventIdx', '')
  }, [watchAdIdx])

  const watchEventIdx = watch('eventIdx')
  useEffect(() => {
    const selectedEvent = participatedEvents.find((event, index) => watchEventIdx && index == Number(watchEventIdx))
    setSelectedEvent(selectedEvent)
  }, [watchEventIdx])

  const watchEmailHostIdx = watch('emailHostIdx')
  const isDirectEmail = watchEmailHostIdx === '-1'
  useEffect(() => {
    const emailHost = EMAIL_HOSTS.find((o, index) => watchEmailHostIdx && index == Number(watchEmailHostIdx))
    if (emailHost) {
      setValue('emailHost', emailHost)
    } else {
      setValue('emailHost', '')
    }
  }, [watchEmailHostIdx])

  const attachments = watch('attachments')

  // const email = useMemo<string>(() => `${emailId}@${emailHost}` [emailId. emailHost])

  const [shouldSubmit, setShouldSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitRef = useRef(false)

  // formData가 변경될 때마다 유효성 검사 실행
  useEffect(() => {
    const result = validateFormData(formData)
    const isValid = Object.keys(result.errors).length === 0
    setShouldSubmit(isValid)
  }, [formData])

  useEffect(() => {
    const fetchData = async () => {
      const api = await getAxiosInstances()

      try {
        const [{ helpRequestTypes }, { participatedAds }] = await Promise.all([
          fetchHelpRequestTypes(legacyParams as LegacyParams),
          fetchParticipatedAds(legacyParams as LegacyParams)
        ])

        setHelpRequestTypes(helpRequestTypes)
        setParticipatedAds(participatedAds)

        if (enableMultiReward) {
          const { participates } = await fetchGlobalParticipatedAds(globalParams as GlobalParams)
          const transformedParticipates = participates.map(
            ad =>
              ({
                type: 'global',
                id: ad.adId,
                campaignId: ad.campaignId,
                title: ad.title,
                events: ad.events,
                createdAt: ad.participatedAt
              }) as ParticipatedAd
          )
          setParticipatedAds(prev => [...prev, ...transformedParticipates])
          // TODO: setParticipatedAds(prev => [...prev, ...transformedParticipates].sort((a, b) => { // createdAt 정렬 }))
        }
      } catch (err) {
        if ((err as any)?.code?.includes('ERR_NETWORK')) {
          sharedWeb.showAlert(
            '잘못된 접근입니다. VPN 혹은 광고 차단 서비스를 사용하고 있다면 해제해 주시길 권장합니다.',
            'SharedWeb.close()'
          )
        }
        console.error('Failed to fetch data:', err)
      }
    }

    fetchData()
  }, [])

  const attachFile = async (
    fileList: FileList | null,
    fileInputRef: RefObject<HTMLInputElement | null>
  ): Promise<File[] | null> => {
    // if (!fileList || fileList?.length) return;
    if (!fileList) {
      return null
    }

    // 안드로이드에서 파일을 1번만 접근 가능한 이슈가 있어서, 메모리로 복사해서 처리
    const newFiles: File[] = []

    for (const file of Array.from(fileList)) {
      const name = file.name

      // 이미 존재하는 파일명 건너뛰기
      if (attachments.find(attachment => attachment.name === name)) continue

      const buffer = await file.arrayBuffer()
      const clone = new File([buffer], file.name, { type: file.type })

      newFiles.push(clone)
    }

    const files = [...attachments, ...newFiles]

    // 파일 개수 체크
    if (files.length > 1) {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      sharedWeb.showAlert('파일은 1개만 첨부할 수 있습니다.')

      return null
      // if (fileInputRef.current) {
      //   fileInputRef.current.value = '';  // input 초기화
      // }
      // alert("파일은 1개만 첨부할 수 있습니다.");
      // setError('attachments', {
      //   type: 'maxLength',
      //   message: '파일은 1개만 첨부할 수 있습니다.'
      // });
      // return;
    }

    const file = files[0]

    if (validateFile(file)) {
      setValue('attachments', files)
      // if (fileNameLabelRef.current) {
      //   fileNameLabelRef.current.textContent = file.name;
      // }

      return files
    }

    return null
  }

  const detachFile = (fileInputRef: RefObject<HTMLInputElement | null>) => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    setValue('attachments', [])
  }

  const validateFile = (file: File) => {
    const MAX_SIZE = 10 * 1024 * 1024 // 10MB
    const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'avi', 'mp4', 'mov', 'heif', 'heic']

    const filename = file.name.toLowerCase()
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => filename.endsWith(ext))

    if (!hasValidExtension) {
      sharedWeb.showAlert('지원하지 않는 파일 형식입니다.')

      return false
    }

    if (file.size > MAX_SIZE) {
      // sharedWeb.showAlert('첨부할 수 있는 용량을 초과했습니다.\n(최대 10MB)', '잠깐! 확인해주세요')
      sharedWeb.showAlert('첨부할 수 있는 용량을 초과했습니다.\n(최대 10MB)')

      return false
    }

    return true
  }

  const validateFormData = (data: SupportFormData) => {
    const errors: FieldErrors<SupportFormData> = {}
    // const errors: Record<string, any> = {};

    if (!data.helpRequestTypeId) {
      errors.helpRequestTypeId = {
        type: 'required',
        message: '문의 종류를 선택해주세요.'
      }
    }

    if (isRequestTypeEvent && !data.adIdx) {
      errors.adIdx = {
        type: 'required',
        message: '참여캠페인을 선택해주세요.'
      }
    }

    if (isRequestTypeEvent && isGlobalCampaign && !data.eventIdx) {
      errors.eventIdx = {
        type: 'required',
        message: '참여미션을 선택해주세요.'
      }
    }

    if (!data.content || data.content.trim() == '') {
      errors.content = {
        type: 'required',
        message: '내용을 입력해주세요.'
      }
    }

    if (!data.emailId) {
      errors.emailId = {
        type: 'required',
        message: '이메일 아이디를 입력해주세요.'
      }
    } else {
      const regex = new RegExp(/^[A-Za-z0-9+_.-]+$/)
      if (!regex.test(data.emailId)) {
        errors.emailId = {
          type: 'pattern',
          message: '이메일 아이디를 확인해주세요.'
        }
      }
    }

    if (!data.emailHostIdx) {
      errors.emailHostIdx = {
        type: 'required',
        message: '이메일 도메인을 선택해주세요.'
      }
    }

    if (isDirectEmail && !data.emailHost) {
      errors.emailHost = {
        type: 'required',
        message: '이메일 도메인을 입력해주세요.'
      }
    } else {
      const regex = new RegExp(/^[a-z0-9][a-z0-9.-]+[a-z0-9]\.[a-z]{2,20}$/)
      if (!regex.test(data.emailHost)) {
        errors.emailHost = {
          type: 'pattern',
          message: '이메일 도메인을 확인해주세요.'
        }
      }
    }

    // TODO: 파일업로드 유효성 검증 추가

    if (!data.agreement) {
      errors.agreement = {
        type: 'required',
        message: '개인 정보 수집 및 이용에 동의해주세요.'
      }
    }

    return {
      values: data,
      errors: Object.keys(errors).length ? errors : {}
    }
  }

  // Submit handler
  const handleSubmit = (
    onSuccess: (data: SupportFormData) => void,
    onError?: (errors: FieldErrors<SupportFormData>) => void
  ) => {
    return hookFormSubmit(
      async data => {
        if (!shouldSubmit || submitRef.current) return
        // setIsSubmitting(true)
        submitRef.current = true
        try {
          if (isGlobalCampaign) {
            const { result } = await submitGlobalSupportForm(data)
            if (result == true) {
              onSuccess?.(data)
            } else {
              sharedWeb.showAlert('잠시 후 다시 이용해주세요')
            }
          } else {
            const { result } = await submitSupportForm(data)
            if (result == true) {
              onSuccess?.(data)
            } else {
              sharedWeb.showAlert('잠시 후 다시 이용해주세요')
            }
          }
        } catch (error) {
          sharedWeb.showAlert('잠시 후 다시 이용해주세요.')
        } finally {
          // setIsSubmitting(false)
          submitRef.current = false
        }
      },
      (errors: typeof formState.errors) => {
        onError?.(errors)
      }
    )()
  }

  const submitSupportForm = async (formData: SupportFormData) => {
    const { emailId, emailHost } = formData
    const email = `${emailId}@${emailHost}`

    const params = {
      ...toSnakeCase(searchParams),

      help_request_type_id: formData.helpRequestTypeId,
      ad_id: selectedAd?.id || '',
      email: email,
      content: formData.content
    }

    const tempFormData = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      tempFormData.append(key, value as string)
    })
    if (formData.attachments.length > 0) {
      tempFormData.append('attachments[]', formData.attachments[0])
    }

    const api = await getAxiosInstances()

    try {
      const response = await api.offerwallApi.post('/help_requests', tempFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60 * 1000
      })

      return response.data
    } catch (err) {
      throw err
    }
  }

  const submitGlobalSupportForm = async (formData: SupportFormData) => {
    const { emailId, emailHost } = formData
    const email = `${emailId}@${emailHost}`
    const subject = helpRequestTypes.find(o => o.id == watchHelpRequestTypeId)?.name

    const params = {
      ...toSnakeCase(searchParams),

      inquiry_type_id: 1,
      campaign_id: selectedAd?.campaignId || '',
      event_id: selectedEvent?.id || '',
      event_title: selectedEvent?.title || '',
      email: email,
      subject: subject,
      body: formData.content
    }

    const tempFormData = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      tempFormData.append(key, value as string)
    })
    if (formData.attachments.length > 0) {
      tempFormData.append('image1', formData.attachments[0])
    }

    const api = await getAxiosInstances()

    try {
      const response = await api.adisonOfwGlobalApi.post('/support_tickets/create', tempFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60 * 1000
      })

      return response.data
    } catch (err) {
      throw err
    }
  }

  return {
    publisher,
    register,
    handleSubmit,
    getValues,
    setValue,
    errors: formState.errors,
    watch,
    helpRequestTypes,
    participatedAds,
    participatedEvents,
    isContentEditable,
    isRequestTypeEvent,
    isGlobalCampaign,
    isDirectEmail,
    attachFile,
    detachFile,
    shouldSubmit,
    isSubmitting,
    rewardServiceNameInFooter,
    headless
  } satisfies UseSupportFormReturn
}
